/**
 * Base Modal Component
 * Reusable modal container with consistent styling and structure.
 */

import React from "react";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";

export interface BaseModalProps {
  styles: Record<string, string>;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  primaryButtonText: string;
  onPrimaryClick: () => void;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  primaryButtonVariant?: "primary" | "danger";
  modalClassName?: string;
}

/**
 * BaseModal - Reusable modal container component
 * Provides consistent structure: header, body (children), and footer with action buttons.
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  styles,
  onClose,
  title,
  children,
  primaryButtonText,
  onPrimaryClick,
  primaryButtonDisabled = false,
  secondaryButtonText,
  primaryButtonVariant = "primary",
  modalClassName,
}) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={modalClassName || styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">{title}</CatHeading>
        </div>
        <div className={styles.modalBody}>{children}</div>
        <div className={styles.modalFooter}>
          <CatButton variant="contrast" onClick={onClose}>
            {secondaryButtonText || "Cancel"}
          </CatButton>
          <CatButton
            variant={primaryButtonVariant}
            onClick={onPrimaryClick}
            disabled={primaryButtonDisabled}
          >
            {primaryButtonText}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default BaseModal;
